import type {
  Product,
  ProductIdInput,
  ProductIndexResponse,
  Variant,
} from '@/lib/services/products/products.types'
import type { LinkedAddOnProduct } from '@/lib/services/add-on-sets/addOnSets.types'
import type { OrganizationIdInput } from '@/lib/organization-scoping'
import type { ComponentProps } from 'react'
import SearchInput from '@/components/form/SearchInput'
import { MediaPlaceholder } from '@/components/patterns/media-placeholder'
import {
  hasSelection,
  toSelectionKey,
  uniqueSelections,
  withoutSelections,
} from '@/components/products/selectionIds'
import { ProductStatus } from '@/lib/enums/ProductStatus'
import { useProducts } from '@/lib/services/products/products.query'
import { formatCurrency, getEnumDisplayName, parseCurrency } from '@/lib/utils'
import { Camera } from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import LoadingSpinner from '@/components/ui/loading-spinner'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Typography } from '@/components/ui/typography'
function normalizeProducts(data: ProductIndexResponse | undefined): Product[] {
  return Array.isArray(data) ? data : (data?.data ?? [])
}

const statusBadgeVariant = {
  [ProductStatus.Active.Value]: 'success',
  [ProductStatus.Draft.Value]: 'warning',
  [ProductStatus.Archived.Value]: 'muted',
} satisfies Record<Product['status'], ComponentProps<typeof Badge>['variant']>

function getDefaultVariant(product: Pick<Product, 'variants'> | null | undefined): Variant | null {
  const variants = Array.isArray(product?.variants) ? product.variants : []

  return variants.find((variant) => variant?.is_default) ?? variants[0] ?? null
}

export default function AddOnProductPicker<
  TInitial extends { id: ProductIdInput } = LinkedAddOnProduct,
>({
  organizationId,
  initialData = [],
  onConfirm,
  onCancel,
}: {
  organizationId?: OrganizationIdInput
  initialData?: TInitial[]
  onConfirm?: (products: (TInitial | Product)[]) => void
  onCancel?: () => void
}) {
  const [search, setSearch] = useState('')
  const { data, isLoading, isError } = useProducts({
    organizationId,
    filters: {
      include: ['media', 'variants'],
      is_add_on: true,
      sort: 'name_asc',
    },
  })
  const addOns = normalizeProducts(data)
  const [selected, setSelected] = useState<ProductIdInput[]>(() =>
    initialData.map((addOn) => addOn.id)
  )
  const normalizedSearch = search.trim().toLowerCase()
  const filteredAddOns = !normalizedSearch
    ? addOns
    : addOns.filter((addOn) => addOn?.name?.toLowerCase()?.includes(normalizedSearch))

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <SearchInput placeholder="Search add-ons..." value={search} setValue={setSearch} />
        <Typography variant="label" tone="muted">
          {selected.length} add-on{selected.length === 1 ? '' : 's'} selected
        </Typography>
      </div>

      <div className="rounded-xl border bg-muted/15">
        <ScrollArea className="h-[420px]">
          {isLoading ? (
            <div className="flex h-full min-h-[240px] items-center justify-center p-6 text-center">
              <LoadingSpinner className="mr-2" /> Loading add-ons...
            </div>
          ) : isError ? (
            <div className="flex h-full min-h-[240px] items-center justify-center p-6 text-center">
              <Typography variant="supporting">Unable to load add-ons.</Typography>
            </div>
          ) : filteredAddOns.length > 0 ? (
            <ul className="px-3 py-2">
              {filteredAddOns.map((addOn, index) => {
                const media = addOn?.media?.[0]
                const defaultVariant = getDefaultVariant(addOn)
                const variantPrice = parseCurrency(
                  defaultVariant?.effective_price ?? defaultVariant?.price ?? 0
                )

                return (
                  <li key={addOn.id}>
                    <Label htmlFor={`add-on-product-${addOn.id}`} appearance="choiceRow">
                      <Checkbox
                        id={`add-on-product-${addOn.id}`}
                        checked={hasSelection(selected, addOn.id)}
                        onCheckedChange={(isChecked) => {
                          if (isChecked) {
                            setSelected((previous) => uniqueSelections([...previous, addOn.id]))
                          } else {
                            setSelected((previous) => withoutSelections(previous, [addOn.id]))
                          }
                        }}
                      />
                      {media?.url ? (
                        <div className="size-14 shrink-0 overflow-hidden rounded-xl border border-border/60 bg-muted/10">
                          <img
                            src={media.url}
                            alt={media.name || addOn.name}
                            className="block h-full w-full object-cover object-center"
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <MediaPlaceholder icon={Camera} size="sm" />
                      )}
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{addOn.name}</span>
                          {addOn?.status ? (
                            <Badge variant={statusBadgeVariant[addOn.status] ?? 'outline'}>
                              {getEnumDisplayName(ProductStatus, addOn.status)}
                            </Badge>
                          ) : null}
                        </div>
                        <Typography variant="label" tone="muted">
                          {defaultVariant?.name || 'Single variant'} ·{' '}
                          {formatCurrency(variantPrice)}
                        </Typography>
                      </div>
                    </Label>
                    {index !== filteredAddOns.length - 1 ? <Separator /> : null}
                  </li>
                )
              })}
            </ul>
          ) : (
            <div className="flex h-full min-h-[240px] items-center justify-center p-6 text-center">
              <Typography variant="supporting">No add-ons found for that search.</Typography>
            </div>
          )}
        </ScrollArea>
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => onCancel?.()}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            const selectedIds = new Set((selected ?? []).map(toSelectionKey))
            const existingIds = new Set(
              (initialData?.map((addOn) => addOn.id) ?? []).map(toSelectionKey)
            )
            const keepExisting = (initialData ?? []).filter((addOn) =>
              selectedIds.has(toSelectionKey(addOn.id))
            )
            const addNew = (addOns ?? []).filter(
              (addOn) =>
                selectedIds.has(toSelectionKey(addOn.id)) &&
                !existingIds.has(toSelectionKey(addOn.id))
            )

            onConfirm?.([...keepExisting, ...addNew])
          }}
        >
          Apply Add-ons
        </Button>
      </div>
    </div>
  )
}
